import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import find from "lodash/find";

import ClubsPV from "./ClubsPV";
import AuthA from "../../store/actions/AuthA";
import QuarterA from "../../store/actions/QuarterA";
import { fetchData } from "../../utils/fetch-data";
import { formatName } from "../../utils/nameUtils";
import { canEditMarks } from "../../utils/workflowStatus";
import paramCase from "param-case";

const _ = { find };

/** Narrows a members or details query to the clubs actually on screen. */
const clubScope = (clubs) =>
  clubs.map((c) => `filter[where][clubId][inq][]=${c.clubId}`).join("&");

/**
 * Clubs, and who is in them.
 *
 * A club is deliberately not a Section. Grades 4 to 6 share one club, while a
 * Section carries a single grade level - and a club modelled as a section would
 * surface everywhere sections are listed: as a class to take attendance for, as
 * a row in the section list, and as a second report card for every member.
 *
 * So membership is keyed on the learner, not on their section. The roster below
 * is built from the school year's section students only to show which grade
 * each member is in; what gets stored is the studentId.
 */
class ClubsPC extends Component {
  state = {
    clubs: [],
    teachers: [],
    // studentId -> { name, gradeLevel, sectionName }
    learners: {},
    selectedClubId: null,
    members: [],
    // clubId -> how many learners are in it, so the list shows at a glance
    // whether anyone has been added.
    memberCounts: {},
    // clubId -> the workflow row for the term being viewed
    details: {},
    // the grade sheet a moderator has chosen but not yet submitted
    clubSheet: null,
    clubSheetName: "",
    loaded: false,
    busy: false,
    error: "",
    notice: "",
  };

  componentDidMount() {
    this.props.authFn.getToken();
    this.props.quarterFn.initializeQuarter();
    this.props.onContentMount({ title: "School Setup / Clubs" });
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.schoolYearId !== this.props.schoolYearId) this.load();
    // The ladder is per term, so changing the term changes whose turn it is.
    if (prevProps.currentQuarter !== this.props.currentQuarter) this.getDetails();
  }

  schoolYearId() {
    return this.props.schoolYearId;
  }

  load() {
    if (!this.schoolYearId()) return;
    // getClubs asks for the member counts and workflow rows once it knows which
    // clubs there are, so that both can be scoped to them.
    this.getClubs();
    this.getTeachers();
    this.getLearners();
  }

  /**
   * Who may do what on this page.
   *
   * Two different jobs, deliberately not one permission. The registrar builds
   * the roster - who is in which club - exactly as they enrol a learner in a
   * section. The marks belong to the club's own moderator, because a club is
   * their subject: nobody else types a subject teacher's grades either.
   *
   * A moderator is not a role on the teacher record; it is whoever the club
   * itself names, so it needs no setting up and cannot drift from the club.
   */
  permissions() {
    const { userType, userId, managesClubs } = this.props;
    const administers =
      ["admin", "principal", "registrar"].indexOf(userType) !== -1 ||
      Boolean(managesClubs);

    // A level head supervises rather than runs: they read every club and
    // every mark, and change neither. Without this they were told they
    // moderate nothing, which was true and unhelpful.
    const seesAll = administers || userType === "levelHead";

    const moderatedClubIds = this.state.clubs
      .filter((c) => c.teacherId && c.teacherId === userId)
      .map((c) => c.clubId);

    return {
      administers: administers,
      seesAll: seesAll,
      moderatedClubIds: moderatedClubIds,
      /**
       * Whether this user may type marks into this club right now.
       *
       * Straight through the shared ladder rule rather than a rule of its own:
       * the moderator owns the marks until they submit, the level head while
       * they are Created, the registrar and principal from Checked onward -
       * the same hands, in the same order, as a subject's grades. A club with
       * no workflow row yet reads as 'To Do', which leaves it with its
       * moderator.
       */
      mayEnterMarks: (club) => {
        if (!club) return false;
        const detail = this.state.details[club.clubId];
        return canEditMarks(detail ? detail.status : "To Do", {
          userType: userType,
          isSubjectTeacher: moderatedClubIds.indexOf(club.clubId) !== -1,
        });
      },
      // A teacher who moderates nothing, administers nothing and supervises
      // nothing has no reason to be here at all.
      mayOpen: seesAll || moderatedClubIds.length > 0,
    };
  }

  getClubs = async () => {
    const res = await fetchData(
      `/clubs?filter[where][active]=true&filter[include]=teacher&filter[where][schoolYearId]=${this.schoolYearId()}`
    );
    if (!res) return this.setState({ loaded: true });

    const clubs = res.data.slice().sort((a, b) => a.name.localeCompare(b.name));
    this.getMemberCounts(clubs);
    this.getDetails(clubs);
    this.setState({ clubs, loaded: true }, () => {
      // Keep the open club open across a reload, but drop a selection that no
      // longer exists - a deleted club would otherwise show a stale roster.
      const stillThere = _.find(clubs, (c) => c.clubId === this.state.selectedClubId);
      if (this.state.selectedClubId && !stillThere) {
        this.setState({ selectedClubId: null, members: [] });
      }
    });
  };

  /**
   * Teacher is the one model in this API behind an ACL - it is based on User,
   * and denies $everyone. Without the access token the moderator list came back
   * 401, fetchData turned that into null, and the dropdown quietly said "No
   * results found" rather than saying it could not load.
   */
  getTeachers = async () => {
    const token = localStorage.getItem("token");
    const res = await fetchData(
      `/teachers?filter[where][active]=true&access_token=${token}`
    );

    if (res && res.data) return this.setState({ teachers: res.data });
    this.setState({
      error:
        "Could not load the teacher list, so no moderator can be chosen. " +
        "Signing out and back in usually fixes it.",
    });
  };

  /**
   * Every learner enrolled this school year, with the grade level they sit in.
   *
   * Taken through sections because that is where a grade level lives; a club
   * spanning Grades 4 to 6 is exactly why the registrar needs to see it while
   * picking. SectionStudent has no school year of its own, so the sections are
   * fetched first and their learners asked for by id.
   */
  getLearners = async () => {
    const sectionsRes = await fetchData(
      `/sections?filter[where][active]=true&filter[where][schoolYearId]=${this.schoolYearId()}`
    );
    if (!sectionsRes || sectionsRes.data.length === 0) return;

    const sections = sectionsRes.data;
    const byId = {};
    sections.forEach((s) => (byId[s.sectionId] = s));

    const ids = sections
      .map((s) => `filter[where][sectionId][inq][]=${s.sectionId}`)
      .join("&");

    const res = await fetchData(
      `/sectionStudents?filter[where][active]=true&filter[include]=student&${ids}`
    );
    if (!res) return;

    const learners = {};
    res.data.forEach((ss) => {
      if (!ss.student) return;
      const section = byId[ss.sectionId];
      learners[ss.studentId] = {
        studentId: ss.studentId,
        name: `${formatName(ss.student.lastName)}, ${formatName(ss.student.firstName)}`,
        gradeLevel: section ? section.gradeLevel : "",
        sectionName: section ? section.name : "",
      };
    });

    this.setState({ learners });
  };

  /**
   * How many learners are in each club, for the list.
   *
   * One request for the whole year rather than one per club: the registrar
   * needs to see at a glance that a club has nobody in it yet, which is the
   * thing they came to the page to fix.
   */
  getMemberCounts = async (clubs) => {
    if (!clubs || clubs.length === 0) return this.setState({ memberCounts: {} });

    const res = await fetchData(
      `/clubStudents?filter[where][active]=true&${clubScope(clubs)}`
    );
    if (!res) return;

    const mine = {};
    clubs.forEach((c) => (mine[c.clubId] = 0));
    res.data.forEach((m) => {
      if (mine[m.clubId] !== undefined) mine[m.clubId] += 1;
    });
    this.setState({ memberCounts: mine });
  };

  /**
   * The workflow row for each club, for the term on screen.
   *
   * One row per club per period, the same shape a section subject's has, so a
   * club's marks climb the same ladder: To Do, Created, Checked, Reviewed,
   * Approved.
   */
  getDetails = async (clubs) => {
    const quarter = parseInt(this.props.currentQuarter, 10);
    const list = clubs || this.state.clubs;
    if (!quarter || !list || list.length === 0) return;

    const res = await fetchData(
      `/clubDetails?filter[where][active]=true&filter[where][type]=grade` +
        `&filter[where][quarter]=${quarter}&${clubScope(list)}`
    );
    if (!res) return;

    const byClub = {};
    res.data.forEach((d) => (byClub[d.clubId] = d));
    this.setState({ details: byClub });
  };

  getMembers = async (clubId) => {
    const res = await fetchData(
      `/clubStudents?filter[where][active]=true&filter[where][clubId]=${clubId}`
    );
    this.setState({ members: res ? res.data : [] });
  };

  write(method, url, data) {
    return axios
      .request({ method, url: `${process.env.REACT_APP_URL}${url}`, data })
      .catch((error) => {
        const message =
          (error.response &&
            error.response.data &&
            error.response.data.error &&
            error.response.data.error.message) ||
          "Could not save. Please try again.";
        this.setState({ error: message });
        throw error;
      });
  }

  /**
   * The grade sheet a moderator attaches before submitting.
   *
   * The stored name is the WHOLE storage key, not just the file's name, and it
   * is built from the club's id rather than its name. Subject sheets store only
   * the filename and rebuild the folder from the subject's code at download
   * time, so renaming or re-pointing a subject orphans every sheet already
   * uploaded - which is exactly how a teacher's sheet went missing in
   * September. A key that is written down once cannot drift.
   */
  handleClubSheetChange = (file) => {
    const club = _.find(this.state.clubs, (c) => c.clubId === this.state.selectedClubId);
    if (!club || !file) return;

    if (file.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
      return this.setState({
        error: "The grade sheet must be an xlsx file. Please save it in xlsx format.",
        clubSheet: null,
        clubSheetName: "",
      });
    }

    const term = parseInt(this.props.currentQuarter, 10) || 1;
    const name = `${paramCase(club.name)}-term-${term}.xlsx`;
    const key = `clubs/${club.clubId}/${name}`;

    this.setState({
      error: "",
      clubSheet: new File([file], key, { type: file.type }),
      clubSheetName: key,
    });
  };

  handleSelectClub = (clubId) => {
    this.setState({
      selectedClubId: clubId,
      members: [],
      error: "",
      notice: "",
      // A sheet chosen for one club must not follow the registrar to another.
      clubSheet: null,
      clubSheetName: "",
    });
    if (clubId) this.getMembers(clubId);
  };

  handleSaveClub = async (club) => {
    this.setState({ busy: true, error: "", notice: "" });
    try {
      if (club.clubId) {
        await this.write("patch", `/clubs/${club.clubId}`, {
          name: club.name,
          teacherId: club.teacherId || null,
          description: club.description || "",
        });
      } else {
        await this.write("post", "/clubs", {
          name: club.name,
          teacherId: club.teacherId || null,
          description: club.description || "",
          schoolYearId: this.schoolYearId(),
        });
      }
      await this.getClubs();
      this.setState({ notice: `Saved ${club.name}.` });
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  /**
   * Clubs are deactivated, never deleted. A past term's marks stay attached to
   * the membership rows, and a removed club would take them with it.
   */
  handleRemoveClub = async (club) => {
    this.setState({ busy: true, error: "", notice: "" });
    try {
      await this.write("patch", `/clubs/${club.clubId}`, { active: false });
      this.setState({ selectedClubId: null, members: [] });
      await this.getClubs();
      this.setState({ notice: `Removed ${club.name}.` });
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  /**
   * Moves a club's marks one step along the ladder, or back to its moderator.
   *
   * The payload matches what the grades screen writes for a subject, so the
   * two audit trails read alike: who did it and when, at every step.
   */
  handleAdvance = async (club, status, notes) => {
    const detail = this.state.details[club.clubId];
    if (!detail) {
      return this.setState({
        error:
          `${club.name} has no workflow row for this term yet, so it cannot be ` +
          "submitted. Ask your administrator to run the club seeding script.",
      });
    }

    const stamp = {
      Created: ["createdBy", "createdDate", "notes"],
      Checked: ["checkedBy", "checkedDate", "levelHeadNotes"],
      Reviewed: ["reviewedBy", "reviewedDate", "registrarNotes"],
      Approved: ["approvedBy", "approvedDate", "principalNotes"],
      Discarded: ["discardedBy", "discardedDate", "discardNotes"],
    }[status];

    const data = { status: status };
    if (stamp) {
      data[stamp[0]] = this.props.userId;
      data[stamp[1]] = new Date();
      if (notes) data[stamp[2]] = notes;
    }

    // A moderator submitting has to attach the sheet, the way a subject
    // teacher does. Approvers moving it further along attach nothing.
    const isSubmission = status === "Created";
    if (isSubmission && !detail.file && !this.state.clubSheet) {
      return this.setState({
        error: `Attach the grade sheet for ${club.name} before submitting it.`,
      });
    }

    this.setState({ busy: true, error: "", notice: "" });

    if (isSubmission && this.state.clubSheet) {
      /*
       * Upload first, and only record the file once it has actually landed.
       * The subject screen used to fire these side by side and swallow the
       * upload's failure, so a sheet that never reached storage still left a
       * row claiming it had - two were lost that way in September. The status
       * waits here for the same reason.
       */
      const fileData = new FormData();
      fileData.append("filePath", this.state.clubSheet);

      try {
        await axios.request({
          method: "post",
          url: `${process.env.REACT_APP_URL}/containers/${process.env.REACT_APP_CONTAINER}/upload`,
          data: fileData,
        });
      } catch (e) {
        this.setState({
          busy: false,
          error:
            "The grade sheet could not be uploaded, so nothing was submitted. " +
            "This is usually the server being briefly unavailable - please try " +
            "again in a moment. The marks have NOT been sent.",
        });
        return;
      }

      data.file = this.state.clubSheetName;
    }

    try {
      await this.write("patch", `/clubDetails/${detail.clubDetailId}`, data);
      await this.getDetails();
      this.setState({
        notice: `${club.name} is now ${status}.`,
        clubSheet: null,
        clubSheetName: "",
      });
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  handleAddMember = async (studentId) => {
    const { selectedClubId, members } = this.state;
    if (!selectedClubId || !studentId) return;
    if (_.find(members, (m) => m.studentId === studentId)) return;

    this.setState({ busy: true, error: "", notice: "" });
    try {
      await this.write("post", "/clubStudents", {
        clubId: selectedClubId,
        studentId: studentId,
      });
      await this.getMembers(selectedClubId);
      await this.getMemberCounts(this.state.clubs);
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  handleRemoveMember = async (member) => {
    this.setState({ busy: true, error: "", notice: "" });
    try {
      await this.write("patch", `/clubStudents/${member.clubStudentId}`, {
        active: false,
      });
      await this.getMembers(this.state.selectedClubId);
      await this.getMemberCounts(this.state.clubs);
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  /** Marks are edited in place, so the row is updated rather than replaced. */
  handleSaveMarks = async (member, field, value) => {
    this.setState({ busy: true, error: "", notice: "" });
    try {
      await this.write("patch", `/clubStudents/${member.clubStudentId}`, {
        [field]: value,
      });
      await this.getMembers(this.state.selectedClubId);
      this.setState({ notice: "Marks saved." });
    } catch (e) {
      /* message already surfaced */
    }
    this.setState({ busy: false });
  };

  render() {
    return (
      <ClubsPV
        {...this.state}
        permissions={this.permissions()}
        auth={this.props.auth}
        userType={this.props.userType}
        currentQuarter={this.props.currentQuarter}
        schoolYear={this.props.schoolYear}
        onSelectClub={this.handleSelectClub}
        onSaveClub={this.handleSaveClub}
        onRemoveClub={this.handleRemoveClub}
        onAddMember={this.handleAddMember}
        onRemoveMember={this.handleRemoveMember}
        onSaveMarks={this.handleSaveMarks}
        onAdvance={this.handleAdvance}
        onClubSheetChange={this.handleClubSheetChange}
        onDismiss={() => this.setState({ error: "", notice: "" })}
      />
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  authFn: AuthA(dispatch),
  quarterFn: QuarterA(dispatch),
});

const mapStateToProps = (state) => ({
  auth: state.AuthR.auth,
  userType: state.AuthR.userType,
  userId: state.AuthR.userId,
  managesClubs: state.AuthR.managesClubs,
  currentQuarter: state.QuarterR.currentQuarter,
  schoolYear: state.QuarterR.schoolYear,
  schoolYearId: state.QuarterR.schoolYearId,
});

export default connect(mapStateToProps, mapDispatchToProps)(ClubsPC);
